import { useState, useEffect } from 'react';
import { X, Car, Train, Bike, Footprints, Clock, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const TRAVEL_MODES = [
  { id: 'car', label: 'Car', icon: Car },
  { id: 'public_transport', label: 'Public transport', icon: Train },
  { id: 'bike', label: 'Bike', icon: Bike },
  { id: 'walking', label: 'Walking', icon: Footprints },
  { id: 'taxi', label: 'Taxi / Rideshare', icon: Car },
  { id: 'not_sure', label: 'Not sure yet', icon: HelpCircle },
];

const CONFIDENCE_CONFIG = {
  on_time: { label: 'On time', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', icon: CheckCircle },
  tight: { label: 'Tight', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Clock },
  likely_late: { label: 'Likely late', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: AlertTriangle },
  pending: { label: 'Pending', color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/20', icon: HelpCircle },
  unknown: { label: 'Unknown', color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/20', icon: HelpCircle },
};

export function TravelConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  request, 
  userCity,
  isLoading 
}) {
  const [step, setStep] = useState(1); // 1: select mode, 2: confirm departure
  const [selectedMode, setSelectedMode] = useState(null);
  const [departureCity, setDepartureCity] = useState(userCity || '');

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedMode(null);
      setDepartureCity(userCity || '');
    }
  }, [isOpen, userCity]);

  if (!isOpen) return null;

  const callTime = request?.timeline?.callTime || request?.startTime || 'TBD';
  const venueCity = request?.city || 'Unknown';

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    if (mode === 'not_sure') {
      // No departure needed — confirm directly. The parent computes the real
      // travel estimate/confidence server-side on confirm.
      onConfirm({ travelMode: 'not_sure', departureCity, confirmedLateArrival: false });
    } else {
      setStep(2);
    }
  };

  const handleConfirm = () => {
    onConfirm({
      travelMode: selectedMode || 'not_sure',
      departureCity: departureCity,
      confirmedLateArrival: false,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative w-full sm:max-w-md pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 bg-[#1A1A1A] border border-white/10 rounded-t-2xl sm:rounded-2xl s8-glow-border"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="font-['Barlow_Condensed'] font-semibold text-white text-lg uppercase tracking-wide">
            {step === 1 && 'How will you travel?'}
            {step === 2 && 'Confirm departure'}
          </h2>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="h-5 w-5 text-white/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          
          {/* Step 1: Select travel mode */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-white/60 mb-4">
                Select how you'll get to <span className="text-white">{venueCity}</span> for the <span className="text-[var(--s8-amber)]">{callTime}</span> call time.
              </p>
              
              <div className="grid grid-cols-2 gap-2">
                {TRAVEL_MODES.map((mode) => {
                  const Icon = mode.icon;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => handleModeSelect(mode.id)}
                      className={cn(
                        'flex flex-col items-center gap-2 p-4 rounded-xl border transition-all',
                        'bg-[#282828] border-white/10 hover:border-[color-mix(in_srgb,var(--s8-amber)_40%,transparent)] hover:bg-[#2C2C2C]',
                        selectedMode === mode.id && 'border-[var(--s8-amber)] bg-[color-mix(in_srgb,var(--s8-amber)_10%,transparent)]'
                      )}
                    >
                      <Icon className={cn(
                        'h-6 w-6',
                        mode.id === 'not_sure' ? 'text-white/40' : 'text-[var(--s8-amber)]'
                      )} />
                      <span className="text-sm text-white font-medium">{mode.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Confirm departure city */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-white/60">
                Where will you be traveling from?
              </p>
              
              <div className="space-y-2">
                <label className="text-xs text-white/40 uppercase tracking-wider">Departure city</label>
                <input
                  type="text"
                  value={departureCity}
                  onChange={(e) => setDepartureCity(e.target.value)}
                  placeholder="e.g., Rotterdam"
                  className="w-full px-4 py-3 bg-[#282828] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[color-mix(in_srgb,var(--s8-amber)_50%,transparent)]"
                />
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-[#282828] border border-white/10">
                <div className="flex-shrink-0">
                  {TRAVEL_MODES.find(m => m.id === selectedMode)?.icon && (
                    <div className="w-10 h-10 rounded-lg bg-[color-mix(in_srgb,var(--s8-amber)_10%,transparent)] flex items-center justify-center">
                      {(() => {
                        const Icon = TRAVEL_MODES.find(m => m.id === selectedMode)?.icon;
                        return Icon ? <Icon className="h-5 w-5 text-[var(--s8-amber)]" /> : null;
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-white font-medium">
                    {TRAVEL_MODES.find(m => m.id === selectedMode)?.label}
                  </div>
                  <div className="text-xs text-white/50">
                    {departureCity || '...'} → {venueCity}
                  </div>
                </div>
              </div>

              <button
                onClick={handleConfirm}
                disabled={!departureCity.trim() || isLoading}
                className={cn(
                  'w-full py-3 rounded-lg font-medium transition-all',
                  departureCity.trim() && !isLoading
                    ? 'bg-[var(--s8-amber)] text-black hover:brightness-110'
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                )}
              >
                {isLoading ? 'Confirming...' : 'Confirm interest'}
              </button>
            </div>
          )}
        </div>

        {/* Step indicator */}
        <div className="flex justify-center gap-1.5 pb-4">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={cn(
                'w-2 h-2 rounded-full transition-colors',
                step >= s ? 'bg-[var(--s8-amber)]' : 'bg-white/20'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Compact travel status badge for venue view
export function TravelStatusBadge({ interest }) {
  if (!interest) return null;
  
  const isConfirmed = interest.travelConfirmed;
  const confidence = interest.travelConfidence;
  const mode = interest.travelMode;
  
  const modeInfo = TRAVEL_MODES.find(m => m.id === mode);
  const confidenceInfo = confidence ? CONFIDENCE_CONFIG[confidence] : null;
  
  return (
    <div className={cn(
      'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium',
      isConfirmed 
        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
        : 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
    )}>
      {modeInfo?.icon && <modeInfo.icon className="h-3.5 w-3.5" />}
      <span>
        {isConfirmed ? 'Travel confirmed' : 'Travel not confirmed yet'}
      </span>
      {confidenceInfo && isConfirmed && (
        <span className={cn('px-1.5 py-0.5 rounded text-[10px]', confidenceInfo.bg, confidenceInfo.color)}>
          {confidenceInfo.label}
        </span>
      )}
    </div>
  );
}

export default TravelConfirmationModal;
